import { Skeleton } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { PromotionBelowCostWarning } from "./below-cost-warning";
import { PromotionTargetPicker } from "./target-picker";

import {
  CheckboxField,
  FormError,
  NumberInputField,
  SelectField,
  SheetSection,
  SideSheet,
  TextInputField,
  notify,
} from "@/components/kit";
import { PromotionType } from "@/enums/promotion";
import { useCreatePromotion } from "@/hooks/api/promotion/useCreatePromotion";
import { useGetPromotion } from "@/hooks/api/promotion/useGetPromotion";
import { useUpdatePromotion } from "@/hooks/api/promotion/useUpdatePromotion";
import {
  PROMOTION_FORM_SCHEMA,
  PromotionFormData,
  emptyPromotionForm,
  toPromotionFormValues,
  toPromotionPayload,
} from "@/schemas/promotion-form.schema";
import { AdminPromotion } from "@/types";
import { getApiErrorMessage } from "@/utils/api-error";
import {
  BUNDLE_TYPES,
  BUY_GET_TYPES,
  ORDER_PROMOTION_TYPES,
  PERCENT_TYPES,
  PROMOTION_TYPE_LABEL,
  changedPromotionFields,
} from "@/utils/promotion";

const TYPE_OPTIONS = Object.values(PromotionType).map((type) => ({
  id: type,
  label: PROMOTION_TYPE_LABEL[type],
}));

const VALUE_LABEL: Record<PromotionType, string> = {
  [PromotionType.PERCENTAGE_OFF]: "Percent off",
  [PromotionType.AMOUNT_OFF]: "AED off each unit",
  [PromotionType.BUY_X_GET_Y]: "Percent off the free units",
  [PromotionType.MIX_AND_MATCH]: "Percent off the free units",
  [PromotionType.BUNDLE_PRICE]: "Bundle price (AED)",
  [PromotionType.ORDER_PERCENTAGE_OFF]: "Percent off the order",
  [PromotionType.ORDER_AMOUNT_OFF]: "AED off the order",
  [PromotionType.FREE_DELIVERY]: "Value",
};

const VALUE_DESCRIPTION: Partial<Record<PromotionType, string>> = {
  [PromotionType.BUY_X_GET_Y]: "100 makes them free",
  [PromotionType.MIX_AND_MATCH]: "100 makes them free",
  [PromotionType.BUNDLE_PRICE]: "Price for every bundle of the units below",
};

const TYPE_DESCRIPTION: Record<PromotionType, string> = {
  [PromotionType.PERCENTAGE_OFF]:
    "Lowers the price of each target product by a percent, e.g. 20% off every unit",
  [PromotionType.AMOUNT_OFF]:
    "Lowers the price of each target product by a fixed AED amount per unit",
  [PromotionType.BUY_X_GET_Y]:
    "Per product: buy X units of the same product, get Y more free or discounted, e.g. buy 1 get 1 free",
  [PromotionType.MIX_AND_MATCH]:
    "Across target products: buy any X units, the cheapest Y in each group are free or discounted",
  [PromotionType.BUNDLE_PRICE]:
    "Any X units from the target products together cost a fixed price, e.g. 3 for AED 10",
  [PromotionType.ORDER_PERCENTAGE_OFF]:
    "Takes a percent off the whole order subtotal, optionally above a minimum",
  [PromotionType.ORDER_AMOUNT_OFF]:
    "Takes a fixed AED amount off the whole order subtotal, optionally above a minimum",
  [PromotionType.FREE_DELIVERY]:
    "Removes the delivery fee, optionally above a minimum subtotal",
};

const PromotionFields = () => {
  const form = useFormContext<PromotionFormData>();
  const type = useWatch({ control: form.control, name: "type" });
  const code = useWatch({ control: form.control, name: "code" });
  const isOrderLevel = ORDER_PROMOTION_TYPES.includes(type);
  const hasCode = code.trim() !== "";

  return (
    <>
      <SheetSection title="Details">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInputField<PromotionFormData>
            isRequired
            label="Name"
            name="name"
            placeholder="20% off dairy"
          />
          <TextInputField<PromotionFormData>
            isRequired
            dir="rtl"
            label="Name (Arabic)"
            name="nameAr"
            placeholder="خصم 20% على الألبان"
          />
        </div>
        <TextInputField<PromotionFormData>
          description="For the team only"
          label="Description"
          name="description"
        />
      </SheetSection>

      <SheetSection title="Discount">
        <SelectField<PromotionFormData>
          description={TYPE_DESCRIPTION[type]}
          label="Type"
          name="type"
          options={TYPE_OPTIONS}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {type !== PromotionType.FREE_DELIVERY ? (
            <NumberInputField<PromotionFormData>
              description={VALUE_DESCRIPTION[type]}
              label={VALUE_LABEL[type]}
              name="value"
              placeholder={PERCENT_TYPES.includes(type) ? "20" : "10.00"}
            />
          ) : null}
          {BUNDLE_TYPES.includes(type) ? (
            <NumberInputField<PromotionFormData>
              description={
                type === PromotionType.BUNDLE_PRICE
                  ? "Units in one bundle, at least 2"
                  : "Units the customer pays for"
              }
              label="Buy quantity"
              name="buyQuantity"
            />
          ) : null}
          {BUY_GET_TYPES.includes(type) ? (
            <NumberInputField<PromotionFormData>
              description="Units the customer gets at the reward price"
              label="Get quantity"
              name="getQuantity"
            />
          ) : null}
          {BUNDLE_TYPES.includes(type) ? (
            <NumberInputField<PromotionFormData>
              description="Leave empty for no limit"
              label="Max per order"
              name="maxApplicationsPerOrder"
            />
          ) : null}
          {isOrderLevel ? (
            <NumberInputField<PromotionFormData>
              description="Subtotal after other promotions. Leave empty for none"
              label="Minimum subtotal (AED)"
              name="minimumSubtotal"
            />
          ) : null}
        </div>
      </SheetSection>

      {!isOrderLevel ? (
        <SheetSection
          description="Products the promotion applies to"
          title="Targets"
        >
          <PromotionTargetPicker />
        </SheetSection>
      ) : null}

      <SheetSection
        description="With a code, customers enter it at checkout. Without one the promotion applies by itself"
        title="Code"
      >
        <TextInputField<PromotionFormData>
          isMono
          label="Code"
          name="code"
          placeholder="SAVE20"
        />
        {hasCode ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberInputField<PromotionFormData>
              description="Across all customers. Empty for no limit"
              label="Total redemptions"
              name="maxRedemptions"
            />
            <NumberInputField<PromotionFormData>
              description="Empty for no limit"
              label="Redemptions per customer"
              name="maxRedemptionsPerUser"
            />
          </div>
        ) : null}
      </SheetSection>

      <SheetSection title="Schedule">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInputField<PromotionFormData>
            isRequired
            label="Starts"
            name="startsAt"
            type="datetime-local"
          />
          <TextInputField<PromotionFormData>
            description={
              hasCode ? "Required with a code" : "Empty to run until turned off"
            }
            label="Ends"
            name="endsAt"
            type="datetime-local"
          />
          <NumberInputField<PromotionFormData>
            description="Higher wins when several promotions match one product"
            label="Priority"
            name="priority"
          />
        </div>
        <CheckboxField<PromotionFormData> label="Active" name="isActive" />
      </SheetSection>
    </>
  );
};

interface PromotionSheetProps {
  isOpen: boolean;
  promotionId: string | null;
  onClose: () => void;
  /** A new promotion that sells products below cost stays open, as an edit, to show the warning. */
  onCreated: (promotion: AdminPromotion) => void;
}

export function PromotionSheet({
  isOpen,
  promotionId,
  onClose,
  onCreated,
}: PromotionSheetProps) {
  const form = useForm<PromotionFormData>({
    resolver: zodResolver(PROMOTION_FORM_SCHEMA),
    mode: "all",
    defaultValues: emptyPromotionForm(),
  });
  const { data: promotion, isLoading } = useGetPromotion(promotionId);
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const mutation = promotionId ? update : create;

  useEffect(() => {
    if (!isOpen) return;

    if (promotionId === null) {
      form.reset(emptyPromotionForm());
    } else if (promotion) {
      form.reset(toPromotionFormValues(promotion));
    }
  }, [isOpen, promotionId, promotion]);

  useEffect(() => {
    if (isOpen) return;

    create.reset();
    update.reset();
  }, [isOpen]);

  const handleError = (error: Error) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      form.setError("code", {
        message: getApiErrorMessage(error, "This code is already used"),
      });
    }
  };

  const handleSaved = (saved: AdminPromotion, message: string) => {
    if (saved.belowCost.count > 0) {
      notify.warning(message, "Some products now sell below cost");

      return;
    }
    notify.success(message);
    onClose();
  };

  const handleSubmit = (data: PromotionFormData) => {
    const payload = toPromotionPayload(data);

    if (promotionId === null) {
      create.mutate(payload, {
        onSuccess: (created) => {
          handleSaved(created, "Promotion created");
          if (created.belowCost.count > 0) onCreated(created);
        },
        onError: handleError,
      });

      return;
    }

    if (!promotion) return;

    const changes = changedPromotionFields(
      payload,
      toPromotionPayload(toPromotionFormValues(promotion)),
    );

    if (Object.keys(changes).length === 0) {
      onClose();

      return;
    }

    update.mutate(
      { id: promotionId, data: changes },
      {
        onSuccess: (updated) => handleSaved(updated, "Promotion saved"),
        onError: handleError,
      },
    );
  };

  const isEdit = promotionId !== null;

  return (
    <FormProvider {...form}>
      <SideSheet
        description={
          promotion?.code && isEdit
            ? `${promotion.code} used in ${promotion.totalRedemptions} checkout${promotion.totalRedemptions === 1 ? "" : "s"}`
            : "Discounts that apply in the cart, by themselves or with a code"
        }
        isOpen={isOpen}
        isSubmitDisabled={isEdit && (!promotion || !form.formState.isDirty)}
        isSubmitting={mutation.isPending}
        submitLabel={isEdit ? "Save" : "Create promotion"}
        title={isEdit ? "Edit promotion" : "Add promotion"}
        onClose={onClose}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormError
          message={
            mutation.error
              ? getApiErrorMessage(
                  mutation.error,
                  "Could not save the promotion, please try again",
                )
              : null
          }
        />
        {isEdit && isLoading ? (
          <div aria-busy="true" className="flex flex-col gap-4" role="status">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        ) : (
          <>
            {promotion && isEdit ? (
              <PromotionBelowCostWarning warning={promotion.belowCost} />
            ) : null}
            <PromotionFields />
          </>
        )}
      </SideSheet>
    </FormProvider>
  );
}
